import { useState, useEffect } from 'react';
import { wordpressAPI } from '../../services/wordpressApi';
import { WPPost } from '../../types/wordpress';
import { MagnifyingGlassIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { decodeHtmlEntities, stripHtml } from '../../utils/htmlEntities';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Card from '../ui/Card';
import LoadingSkeleton from '../ui/LoadingSkeleton';

interface ResourcePickerProps {
  onSelect: (post: WPPost) => void;
  excludeIds?: number[];
  className?: string;
}

export default function ResourcePicker({ onSelect, excludeIds = [], className = '' }: ResourcePickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<WPPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!showPicker) return;

    const searchResources = async () => {
      if (searchQuery.length < 2) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        const posts = await wordpressAPI.fetchPosts({
          search: searchQuery,
          per_page: 20,
        });
        const query = searchQuery.toLowerCase();
        const filtered = posts.filter((post) => {
          if (excludeIds.includes(post.id)) return false;
          const decoded = decodeHtmlEntities(post.title.rendered).toLowerCase();
          return decoded.includes(query) || post.title.rendered.toLowerCase().includes(query);
        });
        setResults(filtered);
      } catch (error) {
        console.error('Error searching resources:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchResources, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, showPicker, excludeIds]);

  const handleSelect = (post: WPPost) => {
    onSelect(post);
    setSearchQuery('');
    setResults([]);
    setShowPicker(false);
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setShowPicker(!showPicker)}
        className="flex items-center gap-2"
      >
        <PlusIcon className="h-5 w-5" />
        Add Resource
      </Button>

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Select Resource</h3>
              <button
                onClick={() => {
                  setShowPicker(false);
                  setSearchQuery('');
                  setResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search resources by title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Type at least 2 characters to search
                </p>
              </div>

              {loading && <LoadingSkeleton lines={3} />}

              {!loading && searchQuery.length >= 2 && results.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No resources found matching "{searchQuery}"</p>
                </div>
              )}

              {!loading && results.length > 0 && (
                <div className="space-y-2">
                  {results.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => handleSelect(post)}
                      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      <h4 className="font-medium text-gray-900 mb-1">
                        {decodeHtmlEntities(post.title.rendered)}
                      </h4>
                      {post.excerpt.rendered && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {stripHtml(post.excerpt.rendered).substring(0, 100)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">ID: {post.id}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
